import type {
  Exercise,
  MovementPattern,
  TimedSessionResult,
  TimeRange,
} from "@heraion/shared";

import { rankE1RMTrends, TREND_PATTERNS, type E1RMTrendRanking } from "./e1rmTrendMath";
import { panelDays, panelRange } from "./progressWindow";
import { computeMovementBalance } from "./tierOneMath";

// PG-64 report pages: the rows of the panels a page can cut, computed in one
// place so the panel and the page plan count the same rows.

/** Rows `[start, end)` of a panel's list: one page of it. */
export type RowSlice = readonly [start: number, end: number];

export const sliceRows = <T>(xs: readonly T[], slice?: RowSlice): readonly T[] =>
  slice ? xs.slice(slice[0], slice[1]) : xs;

type PanelInput = {
  results: TimedSessionResult[];
  catalog: ReadonlyArray<Exercise>;
  range: TimeRange | undefined;
};

export function panelE1RMRanking({ results, catalog, range }: PanelInput): E1RMTrendRanking {
  const r = panelRange(range);
  return rankE1RMTrends(results, catalog, r.toMs, panelDays(r));
}

/** The patterns that get a cell, in TREND_PATTERNS order: an occupant, or
 *  (PG-24) a pattern trained where nothing cleared the bar. Patterns with
 *  nothing logged get none. */
export function trendCellPatterns(
  ranking: Pick<E1RMTrendRanking, "rows" | "unmetPatterns">,
): MovementPattern[] {
  const shown = new Set<MovementPattern>(ranking.unmetPatterns);
  for (const row of ranking.rows) shown.add(row.pattern);
  return TREND_PATTERNS.filter((p) => shown.has(p));
}

export function panelMovementBalance({ results, catalog, range }: PanelInput) {
  const r = panelRange(range);
  return computeMovementBalance(results, catalog, r.toMs, panelDays(r));
}
